import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, withModifiers as _withModifiers, createBlock as _createBlock, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = { class: "flex flex-col h-full" }
const _hoisted_2 = { class: "mb-4" }
const _hoisted_3 = { class: "text-sm font-medium text-gray-500" }
const _hoisted_4 = {
  key: 0,
  class: "flex items-center justify-center py-8"
}
const _hoisted_5 = { class: "text-gray-500" }
const _hoisted_6 = {
  key: 1,
  class: "flex items-center justify-center py-8"
}
const _hoisted_7 = { class: "text-gray-500" }
const _hoisted_8 = {
  key: 2,
  class: "flex-1 flex flex-col min-h-0"
}
const _hoisted_9 = { class: "flex items-center justify-between px-3 py-2 border-b border-gray-200 flex-shrink-0" }
const _hoisted_10 = { class: "text-xs font-semibold text-gray-500 uppercase" }
const _hoisted_11 = { class: "text-xs font-semibold text-gray-500 uppercase" }
const _hoisted_12 = { class: "flex-1 overflow-y-auto space-y-1 mt-2" }
const _hoisted_13 = ["data-dusk", "onClick"]
const _hoisted_14 = { class: "flex items-center flex-1 min-w-0" }
const _hoisted_15 = {
  key: 1,
  class: "w-4 mr-2"
}
const _hoisted_16 = { class: "text-sm font-medium truncate" }
const _hoisted_17 = { class: "flex items-center flex-shrink-0" }

import { computed, PropType, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { Domain } from '@/models';
import { SearchInput } from '@/lib/components/Search';
import { BaseIcon } from '@/lib/components/Icon';
import MedKitBadge from '@/lib/components/Badge/MedKitBadge.vue';

interface DomainTreeItem extends Domain {
  children?: DomainTreeItem[];
}


export default /*@__PURE__*/_defineComponent({
  __name: 'DomainTreePanel',
  props: {
  domains: {
    type: Array as PropType<Domain[]>,
    default: () => []
  },
  selectedDomainId: {
    type: String as PropType<string | null>,
    default: null
  },
  loading: {
    type: Boolean,
    default: false
  }
},
  emits: ["select"],
  setup(__props, { emit: __emit }) {

const props = __props;

const emit = __emit;

const { t } = useI18n();
const search = ref('');
const expandedIds = ref<Set<string>>(new Set());

const buildDomainTree = (domains: Domain[]): DomainTreeItem[] =>
  domains.map((domain) => ({
    ...domain,
    children: domain.subdomains ? buildDomainTree(domain.subdomains) : []
  }));

const filterDomainTree = (items: DomainTreeItem[], searchTerm: string, parentMatched = false): DomainTreeItem[] => {
  const term = searchTerm.toLowerCase();
  const result: DomainTreeItem[] = [];

  for (const item of items) {
    const nameMatches = item.name.toLowerCase().includes(term);
    const shouldIncludeAllChildren = nameMatches || parentMatched;

    const filteredChildren = item.children
      ? filterDomainTree(item.children, searchTerm, shouldIncludeAllChildren)
      : [];

    if (nameMatches || parentMatched || filteredChildren.length > 0) {
      result.push({
        ...item,
        children: shouldIncludeAllChildren ? (item.children ?? []) : filteredChildren
      });
    }
  }

  return result;
};

const domainTree = computed<DomainTreeItem[]>(() => buildDomainTree(props.domains));

const filteredDomainTree = computed<DomainTreeItem[]>(() => {
  if (!search.value) {
    return domainTree.value;
  }
  return filterDomainTree(domainTree.value, search.value);
});

const flattenTree = (items: DomainTreeItem[], depth = 0): Array<{ item: DomainTreeItem; depth: number }> => {
  const result: Array<{ item: DomainTreeItem; depth: number }> = [];
  for (const item of items) {
    result.push({ item, depth });
    if (item.children && item.children.length > 0 && expandedIds.value.has(item.id)) {
      result.push(...flattenTree(item.children, depth + 1));
    }
  }
  return result;
};

const flattenedItems = computed(() => flattenTree(filteredDomainTree.value));

const toggleExpanded = (id: string): void => {
  if (expandedIds.value.has(id)) {
    expandedIds.value.delete(id);
  } else {
    expandedIds.value.add(id);
  }
};

const handleSelect = (id: string): void => {
  if (!expandedIds.value.has(id)) {
    expandedIds.value.add(id);
  }
  emit('select', id);
};

const expandToSelected = (): void => {
  if (!props.selectedDomainId) {
    return;
  }

  const findAndExpandPath = (items: DomainTreeItem[], targetId: string, path: string[] = []): boolean => {
    for (const item of items) {
      if (item.id === targetId) {
        path.forEach((pathId) => expandedIds.value.add(pathId));
        return true;
      }
      if (item.children && item.children.length > 0) {
        if (findAndExpandPath(item.children, targetId, [...path, item.id])) {
          return true;
        }
      }
    }
    return false;
  };

  findAndExpandPath(domainTree.value, props.selectedDomainId);
};

watch(() => props.selectedDomainId, expandToSelected, { immediate: true });
watch(() => props.domains, expandToSelected);

const expandToSearchMatches = (): void => {
  if (!search.value) {
    return;
  }

  const findAndExpandMatchingPaths = (items: DomainTreeItem[], path: string[] = []): void => {
    const term = search.value.toLowerCase();
    for (const item of items) {
      const nameMatches = item.name.toLowerCase().includes(term);
      if (nameMatches) {
        path.forEach((pathId) => expandedIds.value.add(pathId));
      }
      if (item.children && item.children.length > 0) {
        findAndExpandMatchingPaths(item.children, [...path, item.id]);
      }
    }
  };

  findAndExpandMatchingPaths(domainTree.value);
};

watch(search, expandToSearchMatches);

const isExpanded = (id: string): boolean => expandedIds.value.has(id);
const isSelected = (id: string): boolean => props.selectedDomainId === id;
const hasChildren = (item: DomainTreeItem): boolean => Boolean(item.children && item.children.length > 0);
const getOrganisationsCount = (item: DomainTreeItem): number => item.organisations_count ?? 0;

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("span", _hoisted_3, _toDisplayString(_unref(t)('platform.domain-admin.domains.title')), 1)
    ]),
    _createVNode(_unref(SearchInput), {
      modelValue: search.value,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((search).value = $event)),
      class: "mb-4",
      placeholder: _unref(t)('platform.domain-admin.search-subdomain'),
      clear: true,
      "data-dusk": "domain-tree-search"
    }, null, 8, ["modelValue", "placeholder"]),
    (__props.loading)
      ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
          _createElementVNode("span", _hoisted_5, _toDisplayString(_unref(t)('platform.common.loading')), 1)
        ]))
      : (filteredDomainTree.value.length === 0)
        ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
            _createElementVNode("span", _hoisted_7, _toDisplayString(_unref(t)('platform.common.table-no-results')), 1)
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_8, [
            _createElementVNode("div", _hoisted_9, [
              _createElementVNode("span", _hoisted_10, _toDisplayString(_unref(t)('platform.common.name')), 1),
              _createElementVNode("span", _hoisted_11, _toDisplayString(_unref(t)('platform.domain-admin.organisations.title')), 1)
            ]),
            _createElementVNode("div", _hoisted_12, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(flattenedItems.value, ({ item, depth }) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: item.id,
                  class: _normalizeClass(["flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer transition-colors group", [
            isSelected(item.id)
              ? 'bg-primary-100 border border-primary-500'
              : 'hover:bg-gray-100 border border-transparent'
          ]]),
                  style: _normalizeStyle({ marginLeft: `${depth * 0.5}rem` }),
                  "data-dusk": `domain-tree-item-${item.id}`,
                  onClick: ($event: any) => (handleSelect(item.id))
                }, [
                  _createElementVNode("div", _hoisted_14, [
                    (hasChildren(item))
                      ? (_openBlock(), _createBlock(_unref(BaseIcon), {
                          key: 0,
                          name: isExpanded(item.id) ? 'chevron-down' : 'chevron-right',
                          height: "1rem",
                          width: "1rem",
                          class: "text-gray-500 mr-2 flex-shrink-0 cursor-pointer",
                          onClick: _withModifiers(($event: any) => (toggleExpanded(item.id)), ["stop"])
                        }, null, 8, ["name", "onClick"]))
                      : (_openBlock(), _createElementBlock("div", _hoisted_15)),
                    _createElementVNode("span", _hoisted_16, _toDisplayString(item.name), 1)
                  ]),
                  _createElementVNode("div", _hoisted_17, [
                    _createVNode(MedKitBadge, {
                      class: "mr-2",
                      kind: "stable",
                      label: String(getOrganisationsCount(item)),
                      size: "small",
                      "data-dusk": `domain-org-count-${item.id}`
                    }, null, 8, ["label", "data-dusk"])
                  ])
                ], 14, _hoisted_13))
              }), 128))
            ])
          ]))
  ]))
}
}

})